import { useFormikContext } from 'formik';
import { CardContent } from 'shared-components';
import { FormLayout } from 'shared-components';
import { Button } from 'shared-components';
import { ButtonIntent, ButtonTier } from 'shared-components';
import { Checkbox } from 'shared-components';
import { SettingsLogo } from './settings-logo';
import { FontSelector } from './font-selector';
import {
  DEFAULT_GALLERY_DESIGN,
  DEFAULT_GALLERY_DESIGN_LIGHT,
} from './settings.defaults';
import styles from './settings.module.css';

const colourGroups = [
  {
    label: 'General',
    colours: [
      { key: 'backgroundColour', display: 'Background' },
      { key: 'textColour', display: 'Text' },
      { key: 'secondaryTextColour', display: 'Secondary Text' },
      { key: 'tertiaryTextColour', display: 'Tertiary Text' },
    ],
  },
  {
    label: 'Primary Button',
    colours: [
      { key: 'highlightColour', display: 'Background' },
      { key: 'highlightTextColour', display: 'Text' },
    ],
  },
  {
    label: 'Secondary Button',
    colours: [
      { key: 'secondaryButtonColour', display: 'Background' },
      { key: 'secondaryButtonBorderColour', display: 'Border' },
      { key: 'secondaryButtonTextColour', display: 'Text' },
    ],
  },
  {
    label: 'Tertiary Button',
    colours: [
      { key: 'tertiaryButtonColour', display: 'Background' },
      { key: 'tertiaryButtonBorderColour', display: 'Border' },
      { key: 'tertiaryButtonTextColour', display: 'Text' },
    ],
  },
  {
    label: 'Navigation & Modals',
    colours: [
      { key: 'navBackgroundColour', display: 'Nav Background' },
      { key: 'modalBackgroundColour', display: 'Modal Background' },
    ],
  },
  {
    label: 'Inputs',
    colours: [
      { key: 'inputBackgroundColour', display: 'Background' },
      { key: 'inputBorderColour', display: 'Border' },
      { key: 'inputTextColour', display: 'Text' },
    ],
  },
];

const ColourSection = ({
  prefix,
  fontFieldName,
}: {
  prefix: string;
  fontFieldName: string;
}) => {
  const { getFieldProps } = useFormikContext();
  return (
    <div className={styles.colourSections}>
      {colourGroups.map((group) => (
        <div className={styles.colourGroup} key={group.label}>
          <h4 className={styles.colourGroupLabel}>{group.label}</h4>
          <div className={styles.colourGroupItems}>
            {group.colours.map((colour) => (
              <div className={styles.colourItem} key={colour.key}>
                <input
                  type="color"
                  {...getFieldProps(`${prefix}.${colour.key}`)}
                />
                <label htmlFor={`${prefix}.${colour.key}`}>
                  {colour.display}
                </label>
              </div>
            ))}
          </div>
        </div>
      ))}
      <FontSelector fieldName={fontFieldName} />
    </div>
  );
};

export const SettingsDesign = () => {
  const { setFieldValue, values } = useFormikContext<{
    separateLightDarkPalette: boolean;
    [key: string]: unknown;
  }>();
  const separatePalette = values.separateLightDarkPalette;

  return (
    <CardContent>
      <FormLayout>
        <h1>Design</h1>
        <p>Customize the look and feel of your site.</p>
        <SettingsLogo />

        <h2>Colours</h2>
        <Checkbox
          type="checkbox"
          label="Use separate light & dark palettes"
          checked={separatePalette}
          onChange={(e) =>
            setFieldValue('separateLightDarkPalette', e.target.checked)
          }
        />

        {separatePalette ? (
          <div className={styles.paletteColumns}>
            <div className={styles.paletteSection}>
              <h3>Dark Mode</h3>
              <ColourSection
                prefix="galleryDesign"
                fontFieldName="galleryDesign.font"
              />
              <div className={styles.resetButton}>
                <Button
                  intent={ButtonIntent.Negative}
                  tier={ButtonTier.Secondary}
                  onClick={() => {
                    if (
                      // eslint-disable-next-line no-restricted-globals
                      confirm(
                        'Are you sure you want to reset dark mode colours and font to defaults?'
                      )
                    ) {
                      setFieldValue('galleryDesign', DEFAULT_GALLERY_DESIGN);
                    }
                  }}
                >
                  Reset Dark to Defaults
                </Button>
              </div>
            </div>
            <div className={styles.paletteSection}>
              <h3>Light Mode</h3>
              <ColourSection
                prefix="galleryDesignLight"
                fontFieldName="galleryDesignLight.font"
              />
              <div className={styles.resetButton}>
                <Button
                  intent={ButtonIntent.Negative}
                  tier={ButtonTier.Secondary}
                  onClick={() => {
                    if (
                      // eslint-disable-next-line no-restricted-globals
                      confirm(
                        'Are you sure you want to reset light mode colours and font to defaults?'
                      )
                    ) {
                      setFieldValue(
                        'galleryDesignLight',
                        DEFAULT_GALLERY_DESIGN_LIGHT
                      );
                    }
                  }}
                >
                  Reset Light to Defaults
                </Button>
              </div>
            </div>
          </div>
        ) : (
          <>
            <ColourSection
              prefix="galleryDesign"
              fontFieldName="galleryDesign.font"
            />
            <Button
              intent={ButtonIntent.Negative}
              tier={ButtonTier.Secondary}
              onClick={() => {
                if (
                  // eslint-disable-next-line no-restricted-globals
                  confirm(
                    'Are you sure you want to reset all colours and font to defaults?'
                  )
                ) {
                  setFieldValue('galleryDesign', DEFAULT_GALLERY_DESIGN);
                }
              }}
            >
              Reset to Defaults
            </Button>
          </>
        )}
      </FormLayout>
    </CardContent>
  );
};
